/**
 * @file Reads the Overview's goal documents by id, each a single-document
 * listener on `goals/{goalId}` through `useAnalyticsDocsByPaths`. Consumers: the
 * Overview panel (the scope goal), its tables (each person or team goal) and
 * the Home goal progress. A read is found, denied (`permission-denied`),
 * failed, loading or not found; a null id is not found, and a grown id list
 * reads loading rather than "No goal set" until it subscribes.
 */
import { ANALYTICS_GOALS_COLLECTION, type AnalyticsGoal } from '#types';

import type { AnalyticsGoalRead } from './analyticsOverviewTab.types';
import { useAnalyticsDocsByPaths } from './useAnalyticsDocsByPaths';

const pathOf = (goalId: string): string =>
  `${ANALYTICS_GOALS_COLLECTION}/${goalId}`;

export const useAnalyticsGoals = (
  goalIds: readonly (string | null)[],
): ((goalId: string | null) => AnalyticsGoalRead) => {
  const { data, loading, errors } = useAnalyticsDocsByPaths<AnalyticsGoal>(
    goalIds.filter((goalId): goalId is string => !!goalId).map(pathOf),
  );

  return (goalId) => {
    if (!goalId) return { status: 'notFound' };
    const path = pathOf(goalId);
    const goal = data.get(path);
    if (goal !== undefined) return { status: 'found', goal };
    const error = errors.get(path);
    if (error !== undefined)
      return 'code' in error && error.code === 'permission-denied'
        ? { status: 'denied' }
        : { status: 'failed', message: error.message };
    return loading ? { status: 'loading' } : { status: 'notFound' };
  };
};
